import { z } from "zod";

/**
 * Pediatrics: immunization schedule, developmental milestones, and the
 * nutrition / growth-faltering flags. The schedule + milestones + flags are
 * PURE functions of the child's DOB / age / WHO z-scores — no fabricated
 * reference data (z-scores come from the real WHO LMS tables in growth-lms).
 */

// ===== Immunization schedule (Iraq EPI national schedule) =====

export interface VaccineDose {
	/** Stable unique key (vaccine family + dose), e.g. 'penta_1'. */
	key: string;
	/** Vaccine family key for i18n labels, e.g. 'penta'. */
	vaccine: string;
	dose: number;
	/** Recommended age in months from birth. */
	dueMonths: number;
}

/**
 * Iraq national EPI schedule (routine childhood immunizations, 0–6y).
 * Ages are the standard recommended months; "given" is tracked per child.
 */
export const EPI_SCHEDULE: VaccineDose[] = [
	{ key: "bcg_0", vaccine: "bcg", dose: 1, dueMonths: 0 },
	{ key: "opv_0", vaccine: "opv", dose: 0, dueMonths: 0 },
	{ key: "hepb_0", vaccine: "hepb", dose: 0, dueMonths: 0 },
	{ key: "penta_1", vaccine: "penta", dose: 1, dueMonths: 2 },
	{ key: "opv_1", vaccine: "opv", dose: 1, dueMonths: 2 },
	{ key: "pcv_1", vaccine: "pcv", dose: 1, dueMonths: 2 },
	{ key: "rota_1", vaccine: "rota", dose: 1, dueMonths: 2 },
	{ key: "penta_2", vaccine: "penta", dose: 2, dueMonths: 4 },
	{ key: "opv_2", vaccine: "opv", dose: 2, dueMonths: 4 },
	{ key: "pcv_2", vaccine: "pcv", dose: 2, dueMonths: 4 },
	{ key: "rota_2", vaccine: "rota", dose: 2, dueMonths: 4 },
	{ key: "penta_3", vaccine: "penta", dose: 3, dueMonths: 6 },
	{ key: "opv_3", vaccine: "opv", dose: 3, dueMonths: 6 },
	{ key: "measles_1", vaccine: "measles", dose: 1, dueMonths: 9 },
	{ key: "mmr_1", vaccine: "mmr", dose: 1, dueMonths: 12 },
	{ key: "pcv_booster", vaccine: "pcv", dose: 3, dueMonths: 12 },
	{ key: "penta_booster", vaccine: "dtp", dose: 4, dueMonths: 18 },
	{ key: "opv_booster", vaccine: "opv", dose: 4, dueMonths: 18 },
	{ key: "mmr_2", vaccine: "mmr", dose: 2, dueMonths: 18 },
	{ key: "dtp_5", vaccine: "dtp", dose: 5, dueMonths: 54 },
	{ key: "opv_5", vaccine: "opv", dose: 5, dueMonths: 54 },
];

export type ImmunizationStatus = "given" | "due" | "overdue" | "upcoming";

export interface ImmunizationScheduleItem extends VaccineDose {
	status: ImmunizationStatus;
	givenDate: string | null;
}

/**
 * Merge the fixed EPI schedule with the doses a child has received and tag
 * each with status relative to the current age (in months).
 *   given → recorded; overdue → due >1mo ago, not given; due → within the
 *   current month window; upcoming → future.
 */
export function immunizationSchedule(
	ageMonths: number | null,
	givenKeys: Record<string, string>, // key → givenDate (ISO)
): ImmunizationScheduleItem[] {
	return EPI_SCHEDULE.map((d) => {
		const givenDate = givenKeys[d.key] ?? null;
		let status: ImmunizationStatus;
		if (givenDate) status = "given";
		else if (ageMonths == null) status = "upcoming";
		else if (ageMonths >= d.dueMonths + 1) status = "overdue";
		else if (ageMonths >= d.dueMonths) status = "due";
		else status = "upcoming";
		return { ...d, status, givenDate };
	});
}

// ===== Developmental milestones (reference, by age) =====

export type MilestoneDomain = "motor" | "language" | "social" | "cognitive";

export interface Milestone {
	key: string;
	domain: MilestoneDomain;
	/** Age (months) by which the milestone is typically achieved. */
	byMonths: number;
}

/** CDC/WHO-style developmental milestones (0–3y). Reference guidance. */
export const MILESTONES: Milestone[] = [
	{ key: "social_smile", domain: "social", byMonths: 2 },
	{ key: "coos", domain: "language", byMonths: 3 },
	{ key: "head_control", domain: "motor", byMonths: 4 },
	{ key: "rolls_over", domain: "motor", byMonths: 6 },
	{ key: "reaches", domain: "motor", byMonths: 6 },
	{ key: "sits_unsupported", domain: "motor", byMonths: 9 },
	{ key: "babbles", domain: "language", byMonths: 9 },
	{ key: "pulls_to_stand", domain: "motor", byMonths: 12 },
	{ key: "first_words", domain: "language", byMonths: 12 },
	{ key: "waves", domain: "social", byMonths: 12 },
	{ key: "walks_alone", domain: "motor", byMonths: 18 },
	{ key: "several_words", domain: "language", byMonths: 18 },
	{ key: "two_word_phrases", domain: "language", byMonths: 24 },
	{ key: "runs", domain: "motor", byMonths: 24 },
	{ key: "short_sentences", domain: "language", byMonths: 36 },
	{ key: "climbs", domain: "motor", byMonths: 36 },
];

export interface MilestoneView extends Milestone {
	/** True when the child is past the typical age + a 3-month grace window. */
	overdue: boolean;
}

/**
 * Milestones relevant to the current age: those typically achieved by now,
 * each flagged `overdue` when the age exceeds the expected age by >3 months
 * (a soft screening prompt, not a diagnosis).
 */
export function milestonesForAge(ageMonths: number | null): MilestoneView[] {
	if (ageMonths == null) return [];
	return MILESTONES.filter((m) => ageMonths >= m.byMonths).map((m) => ({
		...m,
		overdue: ageMonths > m.byMonths + 3,
	}));
}

// ===== Nutrition + growth-faltering flags (from WHO z-scores) =====

export type NutritionFlag =
	| "underweight"
	| "severe_underweight"
	| "stunting"
	| "severe_stunting"
	| "overweight";

/**
 * Standard WHO malnutrition classification from weight-for-age (wfaZ) and
 * length/height-for-age (lhfaZ) z-scores. <-2 SD moderate, <-3 SD severe.
 */
export function nutritionFlags(
	wfaZ: number | null | undefined,
	lhfaZ: number | null | undefined,
): NutritionFlag[] {
	const flags: NutritionFlag[] = [];
	if (wfaZ != null) {
		if (wfaZ < -3) flags.push("severe_underweight");
		else if (wfaZ < -2) flags.push("underweight");
		else if (wfaZ > 3) flags.push("overweight");
	}
	if (lhfaZ != null) {
		if (lhfaZ < -3) flags.push("severe_stunting");
		else if (lhfaZ < -2) flags.push("stunting");
	}
	return flags;
}

/**
 * Growth faltering between two consecutive weight-for-age z-scores: a drop of
 * ≥0.67 SD crosses a major percentile band and warrants attention.
 */
export function isGrowthFaltering(
	prevWfaZ: number | null | undefined,
	currWfaZ: number | null | undefined,
): boolean {
	if (prevWfaZ == null || currWfaZ == null) return false;
	return prevWfaZ - currWfaZ >= 0.67;
}

// ===== Immunization record contracts =====

export const childImmunizationApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	vaccine_key: z.string(),
	given_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
	notes: z.string().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
});
export type ChildImmunizationApi = z.infer<typeof childImmunizationApiSchema>;

export const recordImmunizationInputSchema = z.object({
	patient_id: z.string().uuid(),
	vaccine_key: z.string().min(1).max(40),
	given_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
	notes: z.string().max(500).optional(),
});
export type RecordImmunizationInput = z.infer<
	typeof recordImmunizationInputSchema
>;
